import { useEffect, useMemo, useState } from "react";

type ActivityType = "PhoneCall" | "Email" | "PatientCalled" | "WorkflowUpdate";

export type PatientRequestActivity = {
  id?: number | string | null;
  activityType?: string | null;
  activityTypeLabel?: string | null;
  outcomeCode?: string | null;
  label?: string | null;
  note?: string | null;
  createdAt?: string | Date | null;
  createdByUserId?: number | null;
  createdByRole?: string | null;
  createdByName?: string | null;
  createdByEmail?: string | null;
  isSystemGenerated?: boolean;
};

type ActivityTypeOption = {
  value: ActivityType;
  label: string;
};

type OutcomeOption = {
  value: string;
  label: string;
  requiresAcceptedStatus?: boolean;
};

type PatientRequestActivityPanelProps = {
  requestId: number | string | null | undefined;
  currentStatus?: string | null;
  actorRole: "FamilyDoctor" | "ClinicAdmin";
  initialActivities?: PatientRequestActivity[] | null;
  disabled?: boolean;
  onActivityAdded?: () => void | Promise<void>;
};

const ACTIVITY_TYPE_OPTIONS: ActivityTypeOption[] = [
  { value: "PhoneCall", label: "Phone call" },
  { value: "Email", label: "Email" },
  { value: "PatientCalled", label: "Patient called" },
  { value: "WorkflowUpdate", label: "Workflow update" },
];

const OUTCOME_OPTIONS_BY_ACTIVITY_TYPE: Record<ActivityType, OutcomeOption[]> = {
  PhoneCall: [
    { value: "NoAnswer", label: "No answer" },
    { value: "LeftVoicemail", label: "Left voicemail" },
    { value: "SpokeToPatient", label: "Spoke to patient" },
    { value: "SpokeToFamily", label: "Spoke to family" },
    { value: "WrongNumber", label: "Wrong number" },
    { value: "NumberNotInService", label: "Number not in service" },
    { value: "PatientDeclined", label: "Patient declined" },
    { value: "CallbackRequested", label: "Callback requested" },
    { value: "BookingCompleted", label: "Booking completed" },
    { value: "FollowUpNeeded", label: "Follow-up needed" },
    { value: "Other", label: "Other" },
  ],

  Email: [
    { value: "EmailSent", label: "Email sent" },
    { value: "FollowUpEmailSent", label: "Follow-up email sent" },
    { value: "PatientReplied", label: "Patient replied" },
    { value: "NoReplyYet", label: "No reply yet" },
    { value: "BookingLinkSent", label: "Booking link sent" },
    { value: "IntakeInstructionsSent", label: "Intake instructions sent" },
    { value: "EmailBounced", label: "Email bounced" },
    { value: "PatientDeclined", label: "Patient declined" },
    { value: "FollowUpNeeded", label: "Follow-up needed" },
    { value: "Other", label: "Other" },
  ],

  PatientCalled: [
    { value: "InquiryAnswered", label: "Inquiry answered" },
    { value: "CallbackRequested", label: "Callback requested" },
    { value: "BookingCompleted", label: "Booking completed" },
    { value: "PatientDeclined", label: "Patient declined" },
    { value: "WrongClinic", label: "Wrong clinic" },
    { value: "FollowUpNeeded", label: "Follow-up needed" },
    { value: "Other", label: "Other" },
  ],

  WorkflowUpdate: [
    {
      value: "MeetGreetBooked",
      label: "Booked for Meet & Greet",
      requiresAcceptedStatus: true,
    },
    { value: "UnderReview", label: "Under review" },
    { value: "WaitingForPatient", label: "Waiting for patient" },
    { value: "IntakePackageSent", label: "Intake package sent" },
    { value: "BookingConfirmed", label: "Booking confirmed" },
    { value: "FollowUpNeeded", label: "Follow-up needed" },
    { value: "Other", label: "Other" },
  ],
};

function normalizeStatus(status: string | null | undefined): string {
  return String(status || "").trim();
}

function isAcceptedStatus(status: string | null | undefined): boolean {
  return normalizeStatus(status).toLowerCase() === "accepted";
}

function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";

  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleString();
}

function activityDisplayLabel(activity: PatientRequestActivity): string {
  const label = String(activity.label || "").trim();
  if (label) return label;

  const outcome = String(activity.outcomeCode || "").trim();
  if (!outcome) return "Activity";

  return outcome
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();
}

function activityActorLabel(activity: PatientRequestActivity): string {
  const by = String(
    activity.createdByName ||
      activity.createdByEmail ||
      activity.createdByRole ||
      ""
  ).trim();

  const role = String(activity.createdByRole || "").trim();

  if (by && role && !by.toLowerCase().includes(role.toLowerCase())) {
    return `${by} · ${role}`;
  }

  return by || "System";
}

function activityKey(activity: PatientRequestActivity, index: number): string {
  const id = activity.id ?? null;
  if (id !== null && id !== undefined && String(id).trim()) {
    return String(id);
  }

  return `${activity.outcomeCode || "activity"}-${
    activity.createdAt || "unknown"
  }-${index}`;
}

function getDefaultOutcome(activityType: ActivityType, isAccepted: boolean) {
  const options = getOutcomeOptions(activityType, isAccepted);
  return options[0]?.value || "";
}

function getOutcomeOptions(
  activityType: ActivityType,
  isAccepted: boolean
): OutcomeOption[] {
  const options = OUTCOME_OPTIONS_BY_ACTIVITY_TYPE[activityType] || [];

  return options.filter((option) => {
    if (option.requiresAcceptedStatus && !isAccepted) return false;
    return true;
  });
}

async function parseJsonSafe(response: Response): Promise<any> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function loadRequestActivities(
  requestId: number | string
): Promise<PatientRequestActivity[]> {
  const res = await fetch(
    `/api/portal/patient-requests/${encodeURIComponent(
      String(requestId)
    )}/activity`,
    {
      credentials: "same-origin",
    }
  );

  const payload = await parseJsonSafe(res);

  if (!res.ok) {
    throw new Error(payload?.error || "Failed to load activity timeline");
  }

  return Array.isArray(payload?.activities) ? payload.activities : [];
}

async function addRequestActivity({
  requestId,
  activityType,
  outcomeCode,
  note,
}: {
  requestId: number | string;
  activityType: ActivityType;
  outcomeCode: string;
  note?: string | null;
}): Promise<PatientRequestActivity | null> {
  const res = await fetch(
    `/api/portal/patient-requests/${encodeURIComponent(
      String(requestId)
    )}/activity`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ activityType, outcomeCode, note }),
    }
  );

  const payload = await parseJsonSafe(res);

  if (!res.ok) {
    throw new Error(payload?.error || "Failed to add activity");
  }

  return payload?.activity || null;
}

export default function PatientRequestActivityPanel({
  requestId,
  currentStatus,
  actorRole,
  initialActivities,
  disabled = false,
  onActivityAdded,
}: PatientRequestActivityPanelProps) {
  const [activities, setActivities] = useState<PatientRequestActivity[]>(
    Array.isArray(initialActivities) ? initialActivities : []
  );
  const [activityType, setActivityType] = useState<ActivityType>("PhoneCall");
  const [outcomeCode, setOutcomeCode] = useState("NoAnswer");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canAdd = !!requestId && !disabled;
  const isAccepted = isAcceptedStatus(currentStatus);

  const outcomeOptions = useMemo(
    () => getOutcomeOptions(activityType, isAccepted),
    [activityType, isAccepted]
  );

  const selectedOutcome = useMemo(
    () => outcomeOptions.find((x) => x.value === outcomeCode) || null,
    [outcomeOptions, outcomeCode]
  );

  const requiresNote = outcomeCode === "Other";
  const canSubmit =
    canAdd &&
    !submitting &&
    !!activityType &&
    !!outcomeCode &&
    (!requiresNote || !!note.trim());

  useEffect(() => {
    setActivities(Array.isArray(initialActivities) ? initialActivities : []);
  }, [initialActivities]);

  useEffect(() => {
    const options = getOutcomeOptions(activityType, isAccepted);
    if (!options.some((x) => x.value === outcomeCode)) {
      setOutcomeCode(options[0]?.value || "");
    }
  }, [activityType, isAccepted, outcomeCode]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!requestId) {
        setActivities([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await loadRequestActivities(requestId);
        if (!cancelled) {
          setActivities(data);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || "Failed to load activity timeline");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [requestId]);

  function handleActivityTypeChange(nextType: ActivityType) {
    setActivityType(nextType);
    setOutcomeCode(getDefaultOutcome(nextType, isAccepted));
    setMessage(null);
    setError(null);
  }

  async function handleAddActivity() {
    if (!requestId || !canSubmit) return;

    setSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      const created = await addRequestActivity({
        requestId,
        activityType,
        outcomeCode,
        note: note.trim() || null,
      });

      setNote("");
      setMessage(`${selectedOutcome?.label || "Activity"} added to timeline.`);

      if (created) {
        setActivities((prev) => [created, ...prev]);
      } else {
        const refreshed = await loadRequestActivities(requestId);
        setActivities(refreshed);
      }

      if (onActivityAdded) {
        await onActivityAdded();
      }
    } catch (e: any) {
      setError(e?.message || "Failed to add activity");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="ocf-section">
      <h3 className="ocf-section-title">Activity</h3>

      <div style={{ marginTop: 14 }}>
        <label
          style={{
            display: "block",
            fontWeight: 900,
            color: "#102a43",
            marginBottom: 8,
          }}
        >
          Activity type
        </label>

        <select
          className="ocf-select"
          value={activityType}
          onChange={(e) =>
            handleActivityTypeChange(e.target.value as ActivityType)
          }
          disabled={!canAdd || submitting}
          style={{ width: "100%" }}
        >
          {ACTIVITY_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <label
          style={{
            display: "block",
            fontWeight: 900,
            color: "#102a43",
            marginTop: 14,
            marginBottom: 8,
          }}
        >
          Action / outcome
        </label>

        <select
          className="ocf-select"
          value={outcomeCode}
          onChange={(e) => {
            setOutcomeCode(e.target.value);
            setMessage(null);
            setError(null);
          }}
          disabled={!canAdd || submitting || outcomeOptions.length === 0}
          style={{ width: "100%" }}
        >
          {outcomeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {activityType === "WorkflowUpdate" && !isAccepted ? (
          <div className="ocf-muted" style={{ marginTop: 8 }}>
            “Booked for Meet &amp; Greet” appears after the request is invited.
          </div>
        ) : null}

        {selectedOutcome?.value === "MeetGreetBooked" ? (
          <div
            style={{
              marginTop: 10,
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid #dbe7f3",
              background: "#f8fbff",
              color: "#334e68",
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            This records that the Meet &amp; Greet appointment was booked. It
            does not change the request status and does not send a patient email.
          </div>
        ) : null}

        <label
          style={{
            display: "block",
            fontWeight: 900,
            color: "#102a43",
            marginTop: 14,
            marginBottom: 8,
          }}
        >
          Note
        </label>

        <textarea
          className="ocf-input"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            setMessage(null);
            setError(null);
          }}
          placeholder="Enter what happened…"
          rows={4}
          style={{ resize: "vertical" }}
          disabled={!canAdd || submitting}
        />

        {requiresNote ? (
          <div className="ocf-muted" style={{ marginTop: 8 }}>
            A note is required when the action / outcome is Other.
          </div>
        ) : null}

        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}
        >
          <button
            type="button"
            className="ocf-btn ocf-btn--primary"
            onClick={handleAddActivity}
            disabled={!canSubmit}
          >
            {submitting ? "Adding Activity…" : "Add Activity"}
          </button>
        </div>

        {message ? (
          <div
            style={{
              marginTop: 10,
              padding: "8px 10px",
              borderRadius: 10,
              border: "1px solid #d1d9e6",
              background: "#f8fbff",
              color: "#102a43",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {message}
          </div>
        ) : null}

        {error ? (
          <div
            style={{
              marginTop: 10,
              padding: "8px 10px",
              borderRadius: 10,
              border: "1px solid #ffd6d6",
              background: "#fff5f5",
              color: "#991b1b",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {error}
          </div>
        ) : null}
      </div>

      <div style={{ marginTop: 18 }}>
        <div className="k">Activity history</div>

        {loading ? (
          <div className="ocf-muted" style={{ marginTop: 8 }}>
            Loading activity timeline…
          </div>
        ) : activities.length === 0 ? (
          <div className="ocf-muted" style={{ marginTop: 8 }}>
            No activity recorded yet.
          </div>
        ) : (
          <div
            style={{
              marginTop: 10,
              display: "grid",
              gap: 10,
            }}
          >
            {activities.map((activity, index) => (
              <div
                key={activityKey(activity, index)}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid #e6edf7",
                  background: activity.isSystemGenerated
                    ? "#f8fafc"
                    : "#ffffff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "baseline",
                  }}
                >
                  <strong style={{ color: "#102a43" }}>
                    {activityDisplayLabel(activity)}
                  </strong>
                  <span className="ocf-muted" style={{ fontSize: 12 }}>
                    {formatDateTime(activity.createdAt)}
                  </span>
                </div>

                <div className="ocf-muted" style={{ marginTop: 4 }}>
                  {activityActorLabel(activity)}
                  {activity.isSystemGenerated ? " · System-generated" : ""}
                </div>

                {activity.note ? (
                  <div className="ocf-note" style={{ marginTop: 8 }}>
                    {activity.note}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}